/**
 * AnalyzerHealthCharts — the Recharts chart wrappers for the per-analyzer health/usage drill-down (MANAGEMENT
 * 15). Three ERP-grade visuals over the gap-tolerant daily health series, each ALWAYS wrapped in a
 * <ResponsiveContainer dir="ltr"> (so the chart never mirrors but the page still RTLs), each token-colored (no
 * raw hex, L-2), each rendering its OWN empty state when there is no snapshot day:
 *   • ResultVolumeChart — stacked result vs error counts per day (the throughput + failure picture).
 *   • TatTrendChart     — avg turnaround (ms) per day (a line; null days break the line as no-data).
 *   • UptimeTrendChart  — daily uptime% (a line clamped 0..100).
 *
 * a11y: each chart sits under a labelled container carrying an aria-label summary; the Recharts SVG is
 * decorative (the same figures live in the drawer's KPI chips + the tooltips). Numerics are LTR. Mirrors the
 * sibling MetricsCharts wrappers verbatim so the two chart regions read identically.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useT } from "@/app";
import { token } from "@/design/theme";
import type { AnalyzerHealthPoint } from "../screens/analyzers.types";
import { HEALTH_COLORS, fmtCount, fmtTat, shortDay } from "../screens/analyzers.lib";

const CHART_HEIGHT = 220;

/** Shared empty placeholder for a chart with no snapshot day yet (role=status, glyph + text). */
function ChartEmpty({ glyph = "◷" }: { glyph?: string }) {
  const { t } = useT();
  return (
    <div
      role="status"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-1)",
        alignItems: "center",
        justifyContent: "center",
        minHeight: CHART_HEIGHT,
        textAlign: "center",
        color: "var(--nv-slate)",
      }}
    >
      <span aria-hidden style={{ fontSize: "var(--nv-fs-h2)", color: "var(--nv-steel)" }}>
        {glyph}
      </span>
      <strong style={{ color: "var(--nv-ink)" }}>{t("mgmt.analyzers.chart.empty.title")}</strong>
      <p style={{ margin: 0, maxWidth: "42ch", fontSize: "var(--nv-fs-caption)" }}>
        {t("mgmt.analyzers.chart.empty.desc")}
      </p>
    </div>
  );
}

const TOOLTIP_STYLE = {
  background: token.surface,
  border: `1px solid ${token.line}`,
  borderRadius: token.rSm,
  color: token.ink,
} as const;

/** Attach the short FR day label to a health point (the shared x-axis). */
function withLabel(series: ReadonlyArray<AnalyzerHealthPoint>) {
  return series.map((p) => ({ ...p, label: shortDay(p.jour) }));
}

// ── Result vs error volume (stacked bars) ─────────────────────────────────────────────

export function ResultVolumeChart({ series }: { series: ReadonlyArray<AnalyzerHealthPoint> }) {
  const { t } = useT();
  if (series.length === 0) return <ChartEmpty glyph="▭" />;
  const data = withLabel(series);
  const total = series.reduce((s, p) => s + p.resultCount + p.errorCount, 0);

  return (
    <div
      style={{ width: "100%", height: CHART_HEIGHT }}
      dir="ltr"
      role="img"
      aria-label={t("mgmt.analyzers.chart.volume.aria", { total })}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={token.lineFaint} vertical={false} />
          <XAxis dataKey="label" stroke={token.slate} fontSize={12} tickLine={false} minTickGap={24} />
          <YAxis allowDecimals={false} stroke={token.slate} fontSize={12} width={44} />
          <Tooltip
            cursor={{ fill: token.lineFaint }}
            formatter={(v: number | string, name: string) => [fmtCount(Number(v)), name]}
            contentStyle={TOOLTIP_STYLE}
          />
          <Bar
            dataKey="resultCount"
            name={t("mgmt.analyzers.chart.volume.results")}
            stackId="v"
            fill={HEALTH_COLORS.results}
            radius={[0, 0, 0, 0]}
            isAnimationActive={false}
          />
          <Bar
            dataKey="errorCount"
            name={t("mgmt.analyzers.chart.volume.errors")}
            stackId="v"
            fill={HEALTH_COLORS.errors}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Turnaround (TAT) line ─────────────────────────────────────────────────────────────

export function TatTrendChart({ series }: { series: ReadonlyArray<AnalyzerHealthPoint> }) {
  const { t } = useT();
  // The TAT line is genuinely empty when NO day carries a TAT (all null) — render the placeholder then.
  const hasAny = series.some((p) => p.avgTatMs != null);
  if (series.length === 0 || !hasAny) return <ChartEmpty glyph="◷" />;
  const data = withLabel(series);

  return (
    <div
      style={{ width: "100%", height: CHART_HEIGHT }}
      dir="ltr"
      role="img"
      aria-label={t("mgmt.analyzers.chart.tat.aria")}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={token.lineFaint} vertical={false} />
          <XAxis dataKey="label" stroke={token.slate} fontSize={12} tickLine={false} minTickGap={24} />
          <YAxis stroke={token.slate} fontSize={12} width={52} tickFormatter={(v: number) => fmtTat(v)} />
          <Tooltip
            cursor={{ stroke: token.line }}
            formatter={(v: number | string) => [fmtTat(Number(v)), t("mgmt.analyzers.chart.tat.tip")]}
            contentStyle={TOOLTIP_STYLE}
          />
          <Line
            type="monotone"
            dataKey="avgTatMs"
            stroke={HEALTH_COLORS.tat}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Uptime% line (clamped 0..100) ─────────────────────────────────────────────────────

export function UptimeTrendChart({ series }: { series: ReadonlyArray<AnalyzerHealthPoint> }) {
  const { t } = useT();
  const hasAny = series.some((p) => p.uptimePct != null);
  if (series.length === 0 || !hasAny) return <ChartEmpty glyph="◔" />;
  const data = withLabel(series);

  return (
    <div
      style={{ width: "100%", height: CHART_HEIGHT }}
      dir="ltr"
      role="img"
      aria-label={t("mgmt.analyzers.chart.uptime.aria")}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={token.lineFaint} vertical={false} />
          <XAxis dataKey="label" stroke={token.slate} fontSize={12} tickLine={false} minTickGap={24} />
          <YAxis domain={[0, 100]} stroke={token.slate} fontSize={12} width={44} unit="%" />
          <Tooltip
            cursor={{ stroke: token.line }}
            formatter={(v: number | string) => [`${v} %`, t("mgmt.analyzers.chart.uptime.tip")]}
            contentStyle={TOOLTIP_STYLE}
          />
          <Line
            type="monotone"
            dataKey="uptimePct"
            stroke={HEALTH_COLORS.uptime}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
